import { createColumnHelper } from '@tanstack/react-table';
import { Box } from 'lucide-react';

import { createStore } from '@/react/kubernetes/datatables/default-kube-datatable-store';

import { Datatable } from '@@/datatables';
import { useTableState } from '@@/datatables/useTableState';
import { Badge } from '@@/Badge';

import { K8sGPUWorkload } from './queries/useGpuQuery';

const columnHelper = createColumnHelper<K8sGPUWorkload>();

const storageKey = 'k8sGpuWorkloadsDatatable';
const settingsStore = createStore(storageKey);

const columns = [
  columnHelper.accessor('Namespace', {
    header: 'Namespace',
    id: 'namespace',
  }),
  columnHelper.accessor('PodName', {
    header: 'Pod',
    id: 'pod',
  }),
  columnHelper.accessor(
    (row) => (row.OwnerName ? `${row.OwnerKind}/${row.OwnerName}` : '-'),
    {
      header: 'Owner',
      id: 'owner',
    }
  ),
  columnHelper.accessor(
    (row) =>
      Object.entries(row.GPURequests)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', '),
    {
      header: 'GPU Requested',
      id: 'gpuRequested',
    }
  ),
  columnHelper.accessor('NodeName', {
    header: 'Node',
    id: 'node',
    cell: ({ getValue }) => getValue() || <span className="text-muted">-</span>,
  }),
  columnHelper.accessor('PodPhase', {
    header: 'Status',
    id: 'podPhase',
    cell: ({ getValue }) => {
      const phase = getValue();
      return <Badge type={phaseBadgeType(phase)}>{phase || 'Unknown'}</Badge>;
    },
  }),
  columnHelper.accessor('SchedulingIssue', {
    header: 'Scheduling Issue',
    id: 'schedulingIssue',
    cell: ({ getValue }) => {
      const issue = getValue();
      if (!issue) return <span className="text-muted">-</span>;
      return (
        <span className="text-warning-9 th-highcontrast:text-yellow-3 th-dark:text-yellow-3">
          {issue}
        </span>
      );
    },
  }),
];

interface Props {
  workloads: K8sGPUWorkload[];
  isLoading: boolean;
}

export function GpuWorkloadsDatatable({ workloads, isLoading }: Props) {
  const tableState = useTableState(settingsStore, storageKey);

  return (
    <Datatable<K8sGPUWorkload>
      disableSelect
      dataset={workloads}
      columns={columns}
      settingsManager={tableState}
      isLoading={isLoading}
      title="GPU Workloads"
      titleIcon={Box}
      getRowId={(row) => `${row.Namespace}/${row.PodName}`}
      emptyContentLabel="No GPU workloads detected"
      data-cy="k8s-gpu-workloads-datatable"
    />
  );
}

function phaseBadgeType(
  phase: string
): 'success' | 'warn' | 'danger' | 'muted' {
  switch (phase) {
    case 'Running':
      return 'success';
    case 'Pending':
      return 'warn';
    case 'Failed':
      return 'danger';
    default:
      return 'muted';
  }
}
